// @todo enable the following disabled rules see OPENTOK-31136 for more info
/* eslint-disable no-param-reassign, global-require, func-names */
import assign from 'lodash/assign';
import pick from 'lodash/pick';
import find from 'lodash/find';
import createAudioFallbackStateMachineDefault from './audioFallbackStateMachine';
import createCongestionLevelEstimatorDefault from './congestionLevel/congestionLevelEstimator';
import createNetworkConditionStateMachineDefault from './congestionLevel/networkConditionStateMachine';
import createNetworkQualityMonitorDefault from './congestionLevel/videoRecovery/networkQualityMonitor';
import createVideoQualityWatcherDefault, { VIDEO_QUALITY_WATCHER_INTERVAL_FAST, VIDEO_QUALITY_WATCHER_INTERVAL_SLOW } from '../../helpers/videoQualityWatcher';
import createPublisherVideoQualityChangedChecker from '../publisher/publisherVideoQualityChangedChecker';
import createLogger from '../../helpers/log';
import eventing from '../../helpers/eventing';
import shouldForceTurn from '../../helpers/shouldForceTurn';
import setICEConfigWithForcedTurn from '../../helpers/setIceConfigWithForcedTurn';
import getMaxBitrateForResolution from './getMaxBitrateForResolution';
import getStatsHelpersDefault from './get_stats_helpers';
import getPeerConnection from './peer_connection';
import getSetCertificates from './set_certificates';
import getWatchAudioAcquisition from './watchAudioAcquisition';
import { CongestionLevels } from './congestionLevel/congestionLevels';
import { NETWORK_CONDITION, NETWORK_CONDITION_REASON } from '../../helpers/networkConditionConstants';
import AudioFallbackVideoStates from '../publisher/audioFallbackVideoStates';
import getDelayForAmrProbeDefault from './congestionLevel/videoRecovery/getDelayForAmrProbe';
import getMaxBitrateArray from '../publisher/getMaxBitrateArray';
import otErrorFactory from '../../helpers/otError';
import ErrorsDefault from '../Errors';
import STATS_SOURCES from './statsSources';
import setDeprecatedProperty from '../../helpers/setDeprecatedProperty';

const { ACTIVE_VIDEO, SUSPENDED_VIDEO } = AudioFallbackVideoStates;

const logging = createLogger('PublisherPeerConnection');

export default function PublisherPeerConnectionFactory(deps = {}) {
  const getStatsHelpers = deps.getStatsHelpers || getStatsHelpersDefault;
  const PeerConnection = deps.PeerConnection || getPeerConnection();
  const setCertificates = deps.setCertificates || getSetCertificates();
  const watchAudioAcquisition = deps.watchAudioAcquisition || getWatchAudioAcquisition;
  const createAudioFallbackStateMachine =
    deps.createAudioFallbackStateMachine || createAudioFallbackStateMachineDefault;
  const createCongestionLevelEstimator =
    deps.createCongestionLevelEstimator || createCongestionLevelEstimatorDefault;
  const createNetworkConditionStateMachine =
    deps.createNetworkConditionStateMachine || createNetworkConditionStateMachineDefault;
  const createNetworkQualityMonitor =
    deps.createNetworkQualityMonitor || createNetworkQualityMonitorDefault;
  const getDelayForAmrProbe = deps.getDelayForAmrProbe || getDelayForAmrProbeDefault;
  const createVideoQualityWatcher =
    deps.createVideoQualityWatcher || createVideoQualityWatcherDefault;
  const otError = deps.otError || otErrorFactory();
  const Errors = deps.Errors || ErrorsDefault;

  /**
   * @typedef {object} PublisherPeerConnectionConfig
   * @property {function(string, string=, object=, object=, boolean=)} logAnalyticsEvent
   * @property {Connection} remoteConnection
   * @property {boolean} reconnection
   * @property {MediaStream} webRTCStream
   * @property {object<string, object>} channels
   * @property {boolean} capableSimulcastStreams
   * @property {boolean} overrideSimulcastEnabled
   * @property {string} subscriberUri
   * @property {object<string, object>} offerOverrides
   * @property {object<string, object>} answerOverrides
   * @property {string} sourceStreamId
   * @property {string} isP2pEnabled
   */

  /**
   * Abstracts PeerConnection related stuff away from Publisher.
   *
   * Responsible for:
   * * setting up the underlying PeerConnection (delegates to PeerConnections)
   * * triggering a connected event when the Peer connection is opened
   * * triggering a disconnected event when the Peer connection is closed
   * * providing a destroy method
   * * providing a processMessage method
   *
   * Once the PeerConnection is connected and the video element playing it triggers
   * the connected event
   *
   * Triggers the following events
   * * connected
   * * disconnected
   *
   * @class PublisherPeerConnection
   * @constructor
   *
   * @param {PublisherPeerConnectionConfig} config
   */
  return function PublisherPeerConnection({
    iceConfig,
    webRTCStream,
    channels,
    sendMessage,
    capableSimulcastStreams,
    overrideSimulcastEnabled,
    sessionSimulcastEnabled,
    enableRidSimulcast,
    logAnalyticsEvent,
    offerOverrides,
    answerOverrides,
    sourceStreamId,
    isP2pEnabled,
    sessionId,
    keyStore,
    sFrameClientStore,
    isE2ee,
    audioFallbackEnabled,
    preferredVideoCodecs,
  }) {
    let _peerConnection;
    let _awaitingIceRestart = false;
    let _cancelWatchAudioAcquisition;
    let audioFallbackStateMachine;
    let congestionLevelEstimator;
    let networkConditionStateMachine;
    let congestionLevelEstimatorEnabled = false;
    let networkQualityMonitor;
    let videoQualityWatcher;
    let videoQualityWatcherEnabled = false;
    let publishStartTime;
    const statsHelpers = getStatsHelpers();

    // Private
    const _onIceRestartAttempt = (ev) => {
      _awaitingIceRestart = true;
      this.trigger('iceRestartAttempt', ev);
    };

    const _onPeerClosed = function () {
      this.destroy();
      if (_awaitingIceRestart) {
        _awaitingIceRestart = false;
        this.trigger('iceRestartFailure');
      }
      this.trigger('disconnected');
    };

    const _onPeerError = function ({ reason, prefix }) {
      this.trigger('error', { reason, prefix });
    };

    const _onIceConnectionStateChange = function (state) {
      if (_awaitingIceRestart && _peerConnection.iceConnectionStateIsConnected()) {
        _awaitingIceRestart = false;
        this.trigger('iceRestartSuccess');
      }

      // watch for the Chrome bug where audio can't be acquired
      // can not use iceConnectionStateIsConnected since it is too broad
      if (state === 'connected') {
        if (!publishStartTime) {
          publishStartTime = Date.now();
        }
        // cancel any pending watcher (in case of ice restart for example)
        if (_cancelWatchAudioAcquisition) {
          _cancelWatchAudioAcquisition();
        }
        _cancelWatchAudioAcquisition = watchAudioAcquisition(
          callback => _peerConnection.getStats(
            null,
            STATS_SOURCES.WATCH_AUDIO_ACQUISITION,
            callback
          ),
          (error) => {
            if (!error) {
              this.trigger('audioAcquisitionProblemResolved');
            } else if (_peerConnection.iceConnectionStateIsConnected()) {
              this.trigger('audioAcquisitionProblem');
            }
          }
        );
        this.trigger('connected');
      }

      this.trigger('iceConnectionStateChange', state);
    };

    this._setVideoTrackEncodingActiveState = async (isActive) => {
      const videoSender = this
        .getSenders()
        .find(({ track }) => track.kind === 'video');
      const parameters = videoSender.getParameters();
      parameters.encodings.forEach((encoding) => {
        // eslint-disable-next-line no-param-reassign
        encoding.active = isActive;
      });
      try {
        await videoSender.setParameters(parameters);
      } catch (e) {
        logging.error(e);
      }
    };

    eventing(this);

    // / Public API

    this.setCongestionLevel = (level) => {
      audioFallbackStateMachine?.setCongestionLevel(level, { algorithm: 'manual' });
    };

    this.getAudioFallbackState = () => audioFallbackStateMachine?.getState();

    this.startEncryption = (connectionId) => {
      if (_peerConnection) {
        _peerConnection.startEncryption(connectionId);
      }
    };

    this.deactivateVideoEncodings = () => {
      if (_peerConnection) {
        _peerConnection.deactivateVideoEncodings();
      }
    };

    this.activateVideoEncodings = () => {
      if (_peerConnection) {
        _peerConnection.activateVideoEncodings();
      }
    };

    this.getDataChannel = function (label, options, completion) {
      _peerConnection.getDataChannel(label, options, completion);
    };

    this.getSourceStreamId = () => sourceStreamId;

    this.destroy = function () {
      congestionLevelEstimator?.stop();
      networkQualityMonitor?.stop();
      videoQualityWatcher?.destroy();
      if (_cancelWatchAudioAcquisition) {
        _cancelWatchAudioAcquisition();
        _cancelWatchAudioAcquisition = null;
      }

      // Clean up our PeerConnection
      if (_peerConnection) {
        _peerConnection.disconnect();
        _peerConnection = null;
      }

      this.trigger('destroyed');

      this.off();
    };

    this.setSendMessage = (send) => {
      _peerConnection.setSendMessage(send);
    };

    this.processMessage = function (type, message) {
      _peerConnection.processMessage(type, message);
    };

    this.addTrack = function (track, stream, callback) {
      return _peerConnection.addTrack(track, stream, callback);
    };

    this.removeTrack = function (RTCRtpSender) {
      return _peerConnection.removeTrack(RTCRtpSender);
    };

    this.getLocalStreams = function () {
      return _peerConnection.getLocalStreams();
    };

    // Init
    this.init = function (rumorIceServers, completion) {
      if (shouldForceTurn(sourceStreamId)) {
        setICEConfigWithForcedTurn(iceConfig);
      }
      const pcConfig = {
        iceConfig: !iceConfig.needRumorIceServersFallback ? iceConfig : assign(iceConfig, {
          servers: [...rumorIceServers, ...iceConfig.servers],
        }),
        channels,
        capableSimulcastStreams,
        overrideSimulcastEnabled,
        sessionSimulcastEnabled,
        enableRidSimulcast,
      };

      setCertificates(pcConfig, (err, pcConfigWithCerts) => {
        if (err) {
          completion(err);
          return;
        }

        const peerConnectionConfig = assign(
          {
            logAnalyticsEvent,
            isPublisher: true,
            offerOverrides,
            answerOverrides,
            sourceStreamId,
            p2p: isP2pEnabled,
            sessionId,
            keyStore,
            sFrameClientStore,
            isE2ee,
            preferredVideoCodecs,
          },
          pcConfigWithCerts
        );

        _peerConnection = new PeerConnection(
          assign({ sendMessage }, peerConnectionConfig)
        );

        _peerConnection.on({
          close: _onPeerClosed,
          error: _onPeerError,
          qos: qos => this.trigger('qos', qos),
          iceConnectionStateChange: _onIceConnectionStateChange,
          iceRestartAttempt: _onIceRestartAttempt,
        }, this);

        _peerConnection.addLocalStream(webRTCStream)
          .then(() => {
            completion(undefined);
          })
          .catch(completion);
      });
    };

    this.setIceConfig = (newIceConfig) => {
      _peerConnection.setIceConfig(newIceConfig);
    };

    this.generateOffer = () => {
      _peerConnection.generateOfferAndSend();
    };

    this.getSenders = function () {
      return _peerConnection.getSenders();
    };

    this.iceRestart = function (reason, forcedRestart) {
      _peerConnection?.iceRestart(reason, forcedRestart);
    };

    this.hasRelayCandidates = () => _peerConnection.hasRelayCandidates();

    this.hasTrack = track => _peerConnection.hasTrack(track);

    this.iceConnectionStateIsConnected = function () {
      return _peerConnection.iceConnectionStateIsConnected();
    };

    this.findAndReplaceTrack = (oldTrack, newTrack) => (
      _peerConnection.findAndReplaceTrack(oldTrack, newTrack)
    );

    this._testOnlyGetFramesEncoded = () => new Promise((resolve, reject) => {
      _peerConnection.getStats(null, STATS_SOURCES.TEST_ONLY_GET_FRAMES_ENCODED, (err, stats) => {
        if (err) {
          reject(err);
          return;
        }

        const videoStat = find(stats, stat => (
          statsHelpers.isVideoStat(stat, stats) && statsHelpers.isOutboundStat(stat)
        ));

        if (!videoStat) {
          reject(new Error('Could not find framesEncoded in getStats report'));
          return;
        }

        resolve(pick(videoStat, ['timestamp', 'framesEncoded']));
      });
    });

    this.getStats = (source, callback) => _peerConnection.getStats(null, source, callback);

    this.getRtcStatsReport = (source, callback) =>
      _peerConnection.getRtcStatsReport(null, source, callback);

    this.processStats = (stats) => {
      const processedStats =
        statsHelpers.normalizeStats(stats, false, publishStartTime || Date.now());
      const transport = {
        ...processedStats.mediaLink?.transport,
        ...networkConditionStateMachine.getTransportStats(),
      };
      const completeStats = {
        ...processedStats,
        mediaLink: {
          ...processedStats.mediaLink,
          transport,
        },
        // TODO transportStats is deprecated and should be removed in favor of mediaLink.transport in a future release, but for now we need to populate both for backwards compatibility
        transportStats: transport,
      };
      setDeprecatedProperty(completeStats, 'transportStats', {
        value: transport,
        name: 'transportStats',
        getWarning: 'This property is deprecated and will be removed in the future. Please use "mediaLink.transport" instead.',
      });
      return completeStats;
    };

    this.setVideoActiveState = (isActive) => {
      this._setVideoTrackEncodingActiveState(isActive);
    };

    this._getVideoSender = () => {
      const sender = this.getSenders().find(({ track: { kind } }) => kind === 'video');
      const sendParams = sender?.getParameters();
      const settings = sender?.track?.getSettings?.() || {};
      return {
        sender, sendParams, settings,
      };
    };

    this._setP2PDefaultMaxBitrate = async () => {
      if (this.getSourceStreamId() !== 'P2P') return;
      const { sender, sendParams, settings: { width, height } } = this._getVideoSender();
      if (!sender || !sendParams) return;

      const maxBitrate = getMaxBitrateForResolution(width, height);
      sendParams.encodings.forEach((encoding) => {
        // eslint-disable-line no-param-reassign
        encoding.maxBitrate = maxBitrate;
      });

      try {
        await sender.setParameters(sendParams);
      } catch (e) {
        // ignore error
      }
    };

    this._setCustomMaxBitrate = async (bitrate) => {
      const { sender, sendParams, settings: { width, height } } = this._getVideoSender();
      if (!sender || !sendParams) return false;

      // -1 means uncapped bitrate
      const maxBitrate = this._isP2p() ? getMaxBitrateForResolution(width, height) : -1;
      const bitrateForEncoding = getMaxBitrateArray(sendParams, width, height, bitrate, maxBitrate);
      if (bitrateForEncoding === undefined) return false;
      // Encodings in ssrc simulcast come from lowest to highest. RID simulcast is the opposite,
      // so we need to reverse the bitrates array.
      if (enableRidSimulcast) {
        bitrateForEncoding.reverse();
      }

      sendParams.encodings.forEach((encoding, encodingIndex) => {
        const encodingBitrate = bitrateForEncoding[encodingIndex];
        encoding.active = encodingBitrate !== 0;
        if (encodingBitrate > 0) {
          // eslint-disable-next-line no-param-reassign
          encoding.maxBitrate = encodingBitrate;
        } else {
          // Deleting the maxBitrate uncaps the bitrate for this encoder
          // eslint-disable-next-line no-param-reassign
          delete encoding.maxBitrate;
        }
      });

      try {
        await sender.setParameters(sendParams);
        return true;
      } catch (e) {
        throw otError(
          Errors.UNEXPECTED_ERROR_CODE,
          new Error('An unexpected error was encountered')
        );
      }
    };

    this._isP2p = () => this.getSourceStreamId() === 'P2P';

    this.setMaxBitrate = async (bitrate) => {
      let didSetCustomBitrate = false;
      if (bitrate === undefined && this._isP2p()) {
        await this._setP2PDefaultMaxBitrate();
      } else if (bitrate !== undefined) {
        didSetCustomBitrate = await this._setCustomMaxBitrate(bitrate);
      }
      return didSetCustomBitrate;
    };

    const onSuspendedVideo = ({ isAmrTransition = false } = {}) => {
      this._setVideoTrackEncodingActiveState(false);
      congestionLevelEstimator?.stop();
      networkQualityMonitor?.start(
        isAmrTransition ? { getDelayForProbeNumber: getDelayForAmrProbe } : {}
      );
      videoQualityWatcher?.stop();
    };

    this.disableCongestionLevelEstimation = () => {
      if (congestionLevelEstimatorEnabled === false) {
        return;
      }
      congestionLevelEstimatorEnabled = false;
      congestionLevelEstimator?.stop();
      networkQualityMonitor?.stop();
      audioFallbackStateMachine?.reset();
    };

    this.enableCongestionLevelEstimation = (amrAudioFallbackState) => {
      if (congestionLevelEstimatorEnabled === true) {
        return;
      }
      congestionLevelEstimatorEnabled = true;
      networkQualityMonitor?.stop();
      if (audioFallbackEnabled) {
        if (amrAudioFallbackState) {
          audioFallbackStateMachine.setInternalState(amrAudioFallbackState);
          if (amrAudioFallbackState.videoState === SUSPENDED_VIDEO) {
            onSuspendedVideo({ isAmrTransition: true });
            return;
          }
        } else {
          audioFallbackStateMachine.setCongestionLevel(CongestionLevels.LOW);
        }
      }
      congestionLevelEstimator?.start();
    };

    this.disableVideoQualityWatcher = () => {
      if (!videoQualityWatcherEnabled) return;
      videoQualityWatcherEnabled = false;
      videoQualityWatcher?.stop();
    };

    this.enableVideoQualityWatcher = (fast) => {
      videoQualityWatcher?.changeInterval(
        fast ? VIDEO_QUALITY_WATCHER_INTERVAL_FAST : VIDEO_QUALITY_WATCHER_INTERVAL_SLOW
      );
      if (videoQualityWatcherEnabled) return;
      videoQualityWatcherEnabled = true;
      if (
        !audioFallbackStateMachine ||
        audioFallbackStateMachine.getState().videoState === ACTIVE_VIDEO
      ) {
        videoQualityWatcher?.start();
      }
    };

    const initAudioFallbackStateMachine = () => {
      audioFallbackStateMachine = createAudioFallbackStateMachine({ logAnalyticsEvent });
      audioFallbackStateMachine.on('stateChange', ({ state }) => {
        switch (state) {
          case ACTIVE_VIDEO:
            this._setVideoTrackEncodingActiveState(true);
            if (videoQualityWatcherEnabled) {
              videoQualityWatcher?.start();
            }
            break;
          case SUSPENDED_VIDEO:
            onSuspendedVideo();
            break;
          default:
            break;
        }
        this.trigger('audioFallbackStateChange', ({
          state,
        }));
      });
    };

    const initCongestionLevelEstimator = () => {
      congestionLevelEstimator = createCongestionLevelEstimator({
        getStats: callback => _peerConnection.getStats(
          null,
          STATS_SOURCES.CONGESTION_LEVEL,
          callback
        ),
        logAnalyticsEvent,
      });
      networkConditionStateMachine = createNetworkConditionStateMachine({
        processStats: this.processStats,
      });
      congestionLevelEstimator.on('congestionLevel', (level, metadata) => {
        audioFallbackStateMachine?.setCongestionLevel(level, metadata);
      });
      congestionLevelEstimator.on('networkCondition', (ev) => {
        networkConditionStateMachine.setNetworkCondition(ev);
      });
      networkConditionStateMachine.on('networkConditionChanged', (ev) => {
        this.trigger('networkConditionChanged', ev);
      });
    };

    const initVideoRecoveryProbeCoordinator = () => {
      networkQualityMonitor = createNetworkQualityMonitor({
        getStats: callback => _peerConnection.getStats(
          null,
          STATS_SOURCES.NETWORK_QUALITY,
          callback
        ),
        setVideoActive: this._setVideoTrackEncodingActiveState,
      });
      networkQualityMonitor.on('networkRecovered', (stats) => {
        audioFallbackStateMachine?.setCongestionLevel(CongestionLevels.LOW);
        networkConditionStateMachine?.setNetworkCondition({
          networkCondition: NETWORK_CONDITION.FAIR,
          networkConditionReason: NETWORK_CONDITION_REASON.UNKNOWN,
          stats,
        });
        congestionLevelEstimator?.start();
      });
    };

    // Do not watch video quality if there is no video track
    if (webRTCStream && webRTCStream.getVideoTracks().length > 0) {
      videoQualityWatcher = createVideoQualityWatcher({
        videoQualityChangedChecker: createPublisherVideoQualityChangedChecker({
          getPcStats:
            cb => _peerConnection.getStats(null, STATS_SOURCES.PUBLISHER_VIDEO_WATCHER, cb),
          parseStats: this.processStats,
          logAnalyticsEvent,
        }),
      });
      videoQualityWatcher.on('videoQualityChanged', (ev) => {
        this.trigger('videoQualityChanged', ev);
      });
    }

    initVideoRecoveryProbeCoordinator();
    initCongestionLevelEstimator();
    if (audioFallbackEnabled) {
      initAudioFallbackStateMachine();
    }
  };
}
